import { apiClient, getErrorMessage } from './apiClient'
import * as indexedDB from './indexedDBService'
import { addToSyncQueue } from './syncService'
import type { Produto } from '../types'
import type { PaginationResponse } from './clienteService'

export interface ProdutoFilters {
  page?: number
  limit?: number
  search?: string
  status?: 'all' | 'ativo' | 'inativo'
  industriaId?: string
  sort?: string
}

export interface ProdutoCreateRequest {
  codigo: string
  nome: string
  industriaId: string
}

// Função auxiliar para obter produtos (exportada para sincronização)
export async function getProdutos(): Promise<Produto[]> {
  const response = await apiClient.get<PaginationResponse<Produto>>('/produtos?limit=9999')
  return response.data.data
}

export const produtoService = {
  async getAll(filters?: ProdutoFilters): Promise<PaginationResponse<Produto>> {
    try {
      if (navigator.onLine) {
        const params = new URLSearchParams()
        if (filters?.page) params.append('page', filters.page.toString())
        if (filters?.limit) params.append('limit', filters.limit.toString())
        if (filters?.search) params.append('search', filters.search)
        if (filters?.status && filters.status !== 'all') params.append('status', filters.status)
        if (filters?.industriaId) params.append('industriaId', filters.industriaId)
        if (filters?.sort) params.append('sort', filters.sort)

        const response = await apiClient.get<PaginationResponse<Produto>>(`/produtos?${params}`)
        return response.data
      }

      const produtos = await indexedDB.getAll<Produto>(indexedDB.STORES.PRODUTOS)
      let filtered = produtos

      if (filters?.search) {
        const searchLower = filters.search.toLowerCase()
        filtered = produtos.filter(p => 
          p.nome?.toLowerCase().includes(searchLower) ||
          p.codigo?.toLowerCase().includes(searchLower)
        )
      }

      if (filters?.industriaId) {
        filtered = filtered.filter(p => p.industriaId === filters.industriaId)
      }

      return {
        data: filtered,
        pagination: {
          page: 1,
          limit: filtered.length,
          total: filtered.length,
          totalPages: 1,
        }
      }
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async getById(id: string): Promise<Produto> {
    try {
      if (navigator.onLine) {
        const response = await apiClient.get<Produto>(`/produtos/${id}`)
        return response.data
      }

      const produto = await indexedDB.get<Produto>(indexedDB.STORES.PRODUTOS, id)
      if (!produto) throw new Error('Produto não encontrado')
      return produto
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async create(data: ProdutoCreateRequest): Promise<Produto> {
    try {
      if (navigator.onLine) {
        const response = await apiClient.post<Produto>('/produtos', data)
        await indexedDB.put(indexedDB.STORES.PRODUTOS, response.data)
        return response.data
      }

      const tempId = `temp_${Date.now()}`
      const produto: Produto = {
        id: tempId,
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Produto

      await indexedDB.put(indexedDB.STORES.PRODUTOS, produto)
      // Adiciona à fila com o ID temporário para referência
      await addToSyncQueue('CREATE', 'produto', { ...data, id: tempId })
      
      return produto
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async update(id: string, data: Partial<ProdutoCreateRequest>): Promise<Produto> {
    try {
      if (navigator.onLine) {
        const response = await apiClient.put<Produto>(`/produtos/${id}`, data)
        await indexedDB.put(indexedDB.STORES.PRODUTOS, response.data)
        return response.data
      }

      const produto = await indexedDB.get<Produto>(indexedDB.STORES.PRODUTOS, id)
      if (!produto) throw new Error('Produto não encontrado')

      const updated = {
        ...produto,
        ...data,
        updatedAt: new Date().toISOString(),
      }

      await indexedDB.put(indexedDB.STORES.PRODUTOS, updated)
      await addToSyncQueue('UPDATE', 'produto', { id, ...data })
      
      return updated
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async delete(id: string): Promise<void> {
    try {
      if (navigator.onLine) {
        await apiClient.delete(`/produtos/${id}`)
        await indexedDB.remove(indexedDB.STORES.PRODUTOS, id)
        return
      }

      await indexedDB.remove(indexedDB.STORES.PRODUTOS, id)
      await addToSyncQueue('DELETE', 'produto', { id })
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },
}
